import Wrapper, { ContentPlaceholder } from "./Support.style";
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import FAQs from "./FAQs";
import { FileNames } from "../../Constants/FileNames";
import FileEndpoints from "../../Services/FileEndpoints";
import FilesLists from '../Shared/FilesLists';
import FilesTitles from '../Shared/FilesTitles';
import Preview from '../Shared/Preview';
import { useLocation } from "react-router-dom";
import {
    EXTENSION_TO_MIME_TYPE,
    parseFilenameFromContentDisposition,
} from "../../Utility/FileResponseUtility";
import { fetchFolderChildrenRecursive } from "../../Utility/FileFolderTreeUtility";

export const Support = () => {
    const [fileName, setFileName] = useState('');
    const [fileId, setFileId] = useState(null);
    const [files, setFiles] = useState([]);
    const objRef = useRef();
    const location = useLocation();
    const path = location.pathname;
    const segments = path.split("/");
    const lastSegment = segments.pop() || segments.pop();
      useEffect(() => {
        const decoded = decodeURIComponent(lastSegment);
        let formatted = "";
    
        if (decoded === "Methodology") formatted = "Methodology";
        else if (decoded === "Product_hierarchy") formatted = "Product Hierarchy"
        else if (decoded === "FAQs") formatted = "FAQs";
    
        if (formatted) {
          setFileName(formatted);
        }
      }, [location.pathname]);

    const [folders, setFolders] = useState([
        {
            fileId: -1,
            title: "FAQs",
            order: 0,
            isFolder: true,
        }
    ]);
    const [showPreview, setShowPreview] = useState(false);
    const [previewItem, setPreviewItem] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [loading, setLoading] = useState(true);

    const filterFolders = useMemo(() => {
        const excludeDataUsePolicy = (f) => f.title?.trim().toLowerCase() !== 'data use policy';
        const filterRec = (list) =>
            list
                .filter((folder) => folder.isFolder && excludeDataUsePolicy(folder))
                .map((folder) =>
                    folder.children ? { ...folder, children: filterRec(folder.children) } : folder
                );
        return filterRec;
    }, []);

    const fetchFolderChildren = useCallback((folder) => {
        return fetchFolderChildrenRecursive(folder, {
            getFiles: (...args) => FileEndpoints.getFiles(...args),
            setFolders,
            onError: (error) => console.error("Error fetching folder children:", error),
        });
    }, []);

    const loadFolders = useCallback(() => {
        setLoading(true);
        FileEndpoints.getFiles(null, FileNames.GGC_SUPPORT, null)
            .then(({ data }) => {
                setFiles(data);

                const fetchedFolders = data
                    .filter(item => item.isFolder)
                    .filter((f) => f.title?.trim().toLowerCase() !== 'data use policy')
                    .sort((a, b) => a.order - b.order);

                // ✅ Immediately set folders so titles are visible early
                setFolders(prevFolders => {
                    const filteredFetched = fetchedFolders.filter(
                        f => !prevFolders.some(p => p.title === f.title)
                    );

                    return [...prevFolders, ...filteredFetched].sort((a, b) => a.order - b.order);
                });

                // 🔁 Load children asynchronously
                const folderPromises = fetchedFolders.map(folder => fetchFolderChildren(folder));
                return Promise.all(folderPromises);
            })
            .catch(error => {
                console.error(error);
            })
            .finally(() => {
                setLoading(false); // ✅ Always reset loading
            });
    }, [fetchFolderChildren]);

    useEffect(() => {
        loadFolders();
    }, [loadFolders]);

    const download = useCallback((e, item) => {
        FileEndpoints.download(item.fileId)
            .then((res) => {
                const filename = parseFilenameFromContentDisposition(res.headers['content-disposition']);

                const dataUrl = window.URL.createObjectURL(new Blob([res.data]));
                let alink = document.createElement('a');
                alink.href = dataUrl;
                alink.download = filename;
                alink.click();
            })
            .catch(error => {
                console.error("Error downloading file:", error);
            });
    }, []);

    const preview = useCallback((e, item) => {
        if (e.target.classList.contains('download-button')) {
            e.preventDefault();
            return;
        }
        setPreviewItem(item);
        setPreviewLoading(true);
        setShowPreview(true);

        FileEndpoints.download(item.fileId)
            .then((res) => {
                const filename = parseFilenameFromContentDisposition(res.headers['content-disposition']);

                const fileExtension = filename.split('.').pop().toLowerCase();
                const contentType = EXTENSION_TO_MIME_TYPE[fileExtension] || 'application/octet-stream';
                item.url = window.URL.createObjectURL(new Blob([res.data], { type: contentType }));
                item.name = filename;
                setPreviewLoading(false);
            })
            .catch(error => {
                console.error("Error fetching preview:", error);
            });
    }, []);

    const renderComponent = (preview, download) => {
        if (fileName === "FAQs" || fileName === "undefined") {
            return <FAQs />;
        }
        else {
            if (loading) {
                return (
                    <ContentPlaceholder>
                        Loading...
                    </ContentPlaceholder>
                );
            }
            if (fileName && fileId) {
                return renderFolders(files, preview, download);
            }

            return (
                <ContentPlaceholder>
                    No document found
                </ContentPlaceholder>
            );
        }
    };

    const findFolderById = (files, fileId) => {
        for (const file of files) {
            if (file?.isFolder && file?.fileId === fileId) {
                return file;
            }
            if (file?.isFolder && Array.isArray(file.children)) {
                const result = findFolderById(file.children, fileId);
                if (result) return result;
            }
        }
        return undefined;
    };

    const renderFolders = useCallback((files, preview, download) => {
        if (!files || files.length === 0 || !fileName || !fileId) return null;

        const matchedFolder = findFolderById(files, fileId);
        if (!matchedFolder) return null;

        const matchingFiles = matchedFolder?.children?.filter(file =>
            !file.isFolder && file.parentFileId === matchedFolder.fileId
        );

        if (!matchingFiles || matchingFiles.length === 0) {
            return (
                <ContentPlaceholder>
                    No document found
                </ContentPlaceholder>
            );
        }

        return (
            <div key={matchedFolder.fileId} id={`Id${matchedFolder.fileId}`}>
                <h2>{matchedFolder.title}</h2>
                <FilesLists
                    folder={{ ...matchedFolder, children: matchingFiles }}
                    searching={""}
                    preview={preview}
                    download={download}
                />
            </div>
        );
    }, [fileName, files, fileId]);

    const fork = useCallback((id, title, fileId) => {
        setFileName(title);
        setFileId(fileId);
    }, []);

    return (
        <Wrapper>
            <Preview
                previewItem={previewItem}
                showPreview={showPreview}
                setShowPreview={setShowPreview}
                setPreviewLoading={setPreviewLoading}
                previewloading={previewLoading}
                download={download}
                objRef={objRef}
            />

            <div className="left">
                <h2>Support</h2>
                <div className="left">
                    <FilesTitles folders={filterFolders(folders)} fork={fork} activeFileName={fileName} />
                </div>
            </div>

            <div className="right">
                <div className="content">
                    {renderComponent(preview, download)}
                </div>
            </div>
        </Wrapper>
    );
};